import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';

/**
 * TournamentPrizePool — prispotten i coins för en turnering (A1).
 *
 * Visar anmälningsavgiften, aktuell pott (avgifter + ev. fast tillskott)
 * och fördelningen per placering. Potten växer live med varje betald
 * anmälan; vid turneringsavslut betalas den ut till placeringarnas
 * kaptener. Renderar null när turneringen saknar både avgift och pott
 * (helt gratis turnering utan tillskott).
 *
 * @param {{tournamentId: string, refreshKey?: number}} props Turneringens id
 *   och en valfri nyckel som triggar omhämtning (t.ex. efter registrering).
 * @returns {JSX.Element|null} Prispott-sektionen.
 */
export default function TournamentPrizePool({ tournamentId, refreshKey = 0 }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    fetch(`${API_URL}/api/tournaments/${tournamentId}/prize-pool`, {
      headers: getAuthHeaders(),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (body) setData(body); })
      .catch(() => {
        // tyst — potten är icke-kritisk för sidan
      });
  }, [tournamentId, getAuthHeaders]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data || !data.enabled) return null;
  if (!data.entry_fee_points && !data.pool_total) return null;

  const locale = currentLocale();
  const medal = (placement) => ({ 1: '🥇', 2: '🥈', 3: '🥉' }[placement] || `#${placement}`);

  return (
    <div className="tournament-prize-pool">
      <div className="prize-pool-header">
        <span className="prize-pool-title">💰 {t('prizePool.title')}</span>
        <span className="prize-pool-total">
          🪙 {data.pool_total.toLocaleString(locale)}
        </span>
      </div>
      {data.entry_fee_points > 0 && (
        <p className="prize-pool-fee">
          {t('prizePool.entryFee', { fee: data.entry_fee_points })}
          {data.my_fee_status === 'paid' && ` ${t('prizePool.feePaid')}`}
        </p>
      )}
      {data.breakdown.length > 0 && data.pool_total > 0 && (
        <div className="prize-pool-breakdown">
          {data.breakdown.map((b) => (
            <div key={b.placement} className="prize-pool-row">
              <span className="prize-pool-place">{medal(b.placement)}</span>
              <span className="prize-pool-pct">{b.pct}%</span>
              <span className="prize-pool-amount">
                🪙 {b.amount.toLocaleString(locale)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
